import { type CancellationReason, type CancellationSource, type ICancellationRow } from './types';

export const REASON_LABELS: Record<CancellationReason, string> = {
	Violence: 'Violence',
	OverVoting: 'Over-voting',
	BvasFailure: 'BVAS failed',
	MaterialsNotDelivered: 'Materials not delivered',
	CourtOrder: 'Court order',
	Other: 'Other',
};

/** An enum name this build does not know is shown as sent, never dropped. */
export const reasonLabel = (reason: string | null | undefined): string =>
	(reason && REASON_LABELS[reason as CancellationReason]) || reason || '—';

export const SOURCE_LABELS: Record<CancellationSource, string> = {
	Agent: 'Field agent',
	Admin: 'Party admin',
};

export const num = (n: number): string => n.toLocaleString('en-NG');

export const plural = (n: number, one: string, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`;

/**
 * `pendingEvidenceCount` counts every upload the phone started and never registered — abandoned
 * sessions included, and it never goes down — so it must not be worded as "still uploading".
 */
export const pendingUploadsLine = (n: number): string =>
	`The agent’s phone started ${plural(n, 'upload')} that ${n === 1 ? 'hasn’t' : 'haven’t'} arrived. The agent can retry ${n === 1 ? 'it' : 'them'} from the cancellation receipt.`;

/** Who put this row on the ledger, in words. Missing `source` = a v1 server: say only the name. */
export const sourceLine = (row: ICancellationRow): string => {
	const who = row.recordedBy && row.recordedBy !== 'null' ? row.recordedBy : null;
	if (row.source === 'Agent') return who ? `Reported by ${who} (field agent)` : 'Reported by a field agent';
	if (row.source === 'Admin') return who ? `Recorded by ${who}` : 'Recorded by a party admin';
	return who ? `Recorded by ${who}` : 'Recorder not recorded';
};

const TIME = new Intl.DateTimeFormat('en-NG', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Lagos' });
const DATE_TIME = new Intl.DateTimeFormat('en-NG', {
	day: 'numeric',
	month: 'short',
	hour: '2-digit',
	minute: '2-digit',
	timeZone: 'Africa/Lagos',
});

const parse = (iso: string | null | undefined): Date | null => {
	if (!iso || iso === 'null') return null;
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? null : d;
};

export const timeOf = (iso: string | null | undefined): string => {
	const d = parse(iso);
	return d ? TIME.format(d) : '—';
};

export const dateTimeOf = (iso: string | null | undefined): string => {
	const d = parse(iso);
	return d ? DATE_TIME.format(d) : '—';
};

export const unitTitle = (row: Pick<ICancellationRow, 'pollingUnitName' | 'pollingUnitCode'>): string =>
	row.pollingUnitName && row.pollingUnitName !== 'null' ? row.pollingUnitName : row.pollingUnitCode;

export const placeLine = (row: ICancellationRow): string =>
	[row.wardName, row.lgaName].filter((p): p is string => !!p && p !== 'null').join(', ') || 'Ward and LGA not on record';

export const STATE_COPY = {
	Withdrawn: 'Withdrawn by the agent',
	Reinstated: 'Reinstated',
} as const;

export const DESKTOP_ALERTS_COPY = {
	offer: 'Get desktop alerts',
	offerDetail: 'A sound and a desktop notification when a unit is cancelled, even when this tab is in the background.',
	denied: 'Desktop notifications are blocked for this site. Alerts still appear here.',
	on: 'Desktop alerts on',
};
